import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues, InputDef, Option } from "./types";
import { defaultValues, isVisible, validateAll } from "./validate";
import { Button, Chip, Spinner } from "../ui/primitives";
import { Icon } from "../ui/Icon";
import {
  CheckboxGroup,
  FileInput,
  RadioGroup,
  SelectedOptionHint,
  SelectInput,
  Switch,
  TextArea,
  TextInput,
} from "../ui/controls";

/**
 * The form engine.
 *
 * Generic by contract: it consumes InputDef[] from ANY pack. It does not import
 * from src/fields and it never branches on a field id. If you find yourself
 * writing `if (fieldId === ...)` in this file, the abstraction has failed — fix
 * the contract in types.ts instead.
 */

export interface FormRendererProps {
  /** Only used to namespace localStorage. Never branched on. */
  fieldId: string;
  inputs: InputDef[];
  /** Prefill (history re-run). Falls back to the pack's declared defaults. */
  initialValues?: FieldValues;
  onSubmit: (values: FieldValues) => void;
  submitting?: boolean;
  /** Quota units this run costs. */
  cost: number;
  remaining?: number | null;
  quotaLimit?: number | null;
  /** Values resolved by server-side autodetect. Applied, then overridable. */
  detected?: Record<string, string>;
  /** Optional cheap pre-call. Returns the detected option value, or null. */
  onDetect?: (input: InputDef, values: FieldValues) => Promise<string | null>;
  submitLabel?: string;
  disabled?: boolean;
}

type Touched = Record<string, boolean>;

const STORAGE_PREFIX = "kullshi:form:";

/** Losing a 5000-word paste to a page reload is unforgivable, so every keystroke
 *  is persisted per field. Files are excluded — a File can't be revived from JSON. */
function loadPersisted(fieldId: string): FieldValues | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + fieldId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as FieldValues) : null;
  } catch {
    return null;
  }
}

function persist(fieldId: string, values: FieldValues): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + fieldId, JSON.stringify(values));
  } catch {
    // Quota or private mode. Persistence is a nicety; never block typing over it.
  }
}

export function clearPersisted(fieldId: string): void {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + fieldId);
  } catch {
    /* ignore */
  }
}

export function FormRenderer({
  fieldId,
  inputs,
  initialValues,
  onSubmit,
  submitting = false,
  cost,
  remaining,
  quotaLimit,
  detected,
  onDetect,
  submitLabel = "شغّلها",
  disabled = false,
}: FormRendererProps) {
  const [values, setValues] = useState<FieldValues>(() => ({
    ...defaultValues(inputs),
    ...(loadPersisted(fieldId) ?? {}),
    ...(initialValues ?? {}),
  }));
  const [touched, setTouched] = useState<Touched>({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [detecting, setDetecting] = useState<Record<string, boolean>>({});
  const [detectedLocal, setDetectedLocal] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Re-seed when the form is pointed at a different field or a history re-run.
  useEffect(() => {
    setValues({
      ...defaultValues(inputs),
      ...(loadPersisted(fieldId) ?? {}),
      ...(initialValues ?? {}),
    });
    setTouched({});
    setShowAllErrors(false);
    setDetectedLocal({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, initialValues]);

  useEffect(() => {
    persist(fieldId, values);
  }, [fieldId, values]);

  // Server-resolved autodetect values land here after a run; the user keeps the
  // final say, so they're written into the form as normal, overridable values.
  useEffect(() => {
    if (!detected) return;
    const entries = Object.entries(detected);
    if (entries.length === 0) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const [k, v] of entries) next[k] = v;
      return next;
    });
    setDetectedLocal((prev) => ({ ...prev, ...detected }));
  }, [detected]);

  const errors = useMemo(() => validateAll(inputs, values), [inputs, values]);
  const shown = useMemo(() => inputs.filter((i) => isVisible(i, values)), [inputs, values]);

  const setValue = useCallback((name: string, v: FieldValues[string]) => {
    setValues((prev) => ({ ...prev, [name]: v }));
  }, []);

  function errorFor(input: InputDef): string | undefined {
    if (!showAllErrors && !touched[input.name]) return undefined;
    return errors[input.name];
  }

  async function runDetect(input: InputDef) {
    if (!onDetect || !input.autodetect) return;
    const source = values[input.autodetect.from];
    if (!source || String(source).trim() === "") return;
    setDetecting((p) => ({ ...p, [input.name]: true }));
    try {
      const found = await onDetect(input, values);
      if (found && input.options?.some((o) => o.value === found)) {
        setValue(input.name, found);
        setDetectedLocal((p) => ({ ...p, [input.name]: found }));
      }
    } finally {
      setDetecting((p) => ({ ...p, [input.name]: false }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setShowAllErrors(true);
    const errs = validateAll(inputs, values);
    const firstBad = shown.find((i) => errs[i.name]);
    if (firstBad) {
      const node = nodeRefs.current[firstBad.name];
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
      node?.querySelector<HTMLElement>("input, textarea, select, [tabindex]")?.focus({
        preventScroll: true,
      });
      return;
    }
    onSubmit(values);
  }

  const errorCount = shown.filter((i) => errors[i.name]).length;
  const quotaShort = typeof remaining === "number" && remaining < cost;

  return (
    <form onSubmit={handleSubmit} noValidate className="grid gap-7">
      {shown.map((input) => (
        <div
          key={input.name}
          ref={(el) => {
            nodeRefs.current[input.name] = el;
          }}
          className="md-enter scroll-mt-24"
        >
          <InputRow
            input={input}
            value={values[input.name]}
            error={errorFor(input)}
            detectedValue={detectedLocal[input.name]}
            detecting={Boolean(detecting[input.name])}
            canDetect={Boolean(onDetect && input.autodetect)}
            fileName={fileNames[input.name]}
            disabled={disabled || submitting}
            onDetect={() => void runDetect(input)}
            onChange={(v) => {
              setValue(input.name, v);
              // A value the user picked is theirs, not ours — drop the badge.
              if (detectedLocal[input.name]) {
                setDetectedLocal((p) => {
                  const next = { ...p };
                  delete next[input.name];
                  return next;
                });
              }
            }}
            onFile={(file) => {
              setFileNames((p) => ({ ...p, [input.name]: file?.name ?? "" }));
              setValue(input.name, file?.name ?? "");
            }}
            onBlur={() => setTouched((p) => ({ ...p, [input.name]: true }))}
          />
        </div>
      ))}

      <div className="sticky bottom-0 -mx-4 mt-2 border-t border-outline-variant bg-surface/95 px-4 py-4 backdrop-blur-sm sm:-mx-6 sm:px-6">
        {showAllErrors && errorCount > 0 ? (
          <p role="alert" className="md-body-small mb-3 flex items-center gap-1.5 text-error">
            <Icon name="error" size={16} filled />
            في {errorCount === 1 ? "سؤال" : `${errorCount} أسئلة`} محتاج تكملة.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            icon={submitting ? undefined : "play_arrow"}
            loading={submitting}
            disabled={disabled || quotaShort}
            className="min-w-[10rem]"
          >
            {submitting ? "قاعد يشتغل…" : submitLabel}
          </Button>

          <div className="md-body-small flex flex-wrap items-center gap-2 text-on-surface-variant">
            <Chip tone="neutral" icon="bolt">
              الكلفة: <bdi>{cost}</bdi> {cost === 1 ? "وحدة" : "وحدات"}
            </Chip>
            {typeof remaining === "number" ? (
              <Chip tone={quotaShort ? "error" : "neutral"} icon="account_balance_wallet">
                باقي لك: <bdi>{remaining}</bdi>
                {typeof quotaLimit === "number" ? (
                  <>
                    {" "}
                    من <bdi>{quotaLimit}</bdi>
                  </>
                ) : null}
              </Chip>
            ) : null}
          </div>
        </div>

        {quotaShort ? (
          <p className="md-body-small mt-2 text-error">
            ما بقت عندك وحدات كافية لهذا التشغيل. سجّل دخول حتى تكمل.
          </p>
        ) : null}
      </div>
    </form>
  );
}

// ─────────────────────────── One input ───────────────────────────

function InputRow({
  input,
  value,
  error,
  detectedValue,
  detecting,
  canDetect,
  fileName,
  disabled,
  onChange,
  onFile,
  onBlur,
  onDetect,
}: {
  input: InputDef;
  value: FieldValues[string];
  error?: string;
  detectedValue?: string;
  detecting: boolean;
  canDetect: boolean;
  fileName?: string;
  disabled: boolean;
  onChange: (v: FieldValues[string]) => void;
  onFile: (f: File | null) => void;
  onBlur: () => void;
  onDetect: () => void;
}) {
  const id = `in-${input.name}`;
  const labelId = `${id}-label`;
  const hintId = input.hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const counterId = input.maxLength ? `${id}-count` : undefined;
  const describedBy = [hintId, errId, counterId].filter(Boolean).join(" ") || undefined;
  const invalid = Boolean(error);

  const isGroup = input.type === "radio" || input.type === "multiselect" || input.type === "toggle";
  const strValue = typeof value === "string" ? value : value === undefined ? "" : String(value);
  const len = typeof value === "string" ? value.length : 0;
  const overLimit = Boolean(input.maxLength && len > input.maxLength);

  // Which option means "detect it for me"? types.ts doesn't say (see the
  // contract-gap note in the build report), so it's inferred without ever
  // hardcoding a literal: the pack's declared default if that's a real option,
  // otherwise the first one — both hold for every pack we ship.
  const autoOptionValue = resolveAutoOption(input);
  const isAwaitingDetection = Boolean(
    input.autodetect && autoOptionValue !== undefined && strValue === autoOptionValue,
  );

  const label = (
    <>
      <span className="text-on-surface">{input.label}</span>
      {input.required ? (
        <span className="text-error" aria-hidden="true">
          {" "}
          *
        </span>
      ) : null}
    </>
  );

  return (
    <div>
      {/* Toggles put their label beside the switch, not above it. */}
      {input.type !== "toggle" ? (
        isGroup ? (
          <p id={labelId} className="md-title-medium mb-1">
            {label}
          </p>
        ) : (
          <label id={labelId} htmlFor={id} className="md-title-medium mb-1 block">
            {label}
          </label>
        )
      ) : null}

      {/* Supporting text. The hints ARE the teaching — this is why the form
          beats a blank box, so it renders at body-small in a real color, not
          as 10px grey furniture. */}
      {input.hint && input.type !== "toggle" ? (
        <p id={hintId} className="md-body-small mb-3 flex items-start gap-1.5 text-on-surface-variant">
          <Icon name="lightbulb" size={15} className="mt-1 shrink-0 text-tertiary" filled />
          <span>{input.hint}</span>
        </p>
      ) : null}

      {input.type === "text" ? (
        <TextInput
          id={id}
          value={strValue}
          placeholder={input.placeholder}
          maxLength={input.maxLength}
          invalid={invalid}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : null}

      {input.type === "textarea" ? (
        <TextArea
          id={id}
          value={strValue}
          placeholder={input.placeholder}
          invalid={invalid || overLimit}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : null}

      {input.type === "number" ? (
        <TextInput
          id={id}
          type="text"
          inputMode="numeric"
          value={strValue}
          placeholder={input.placeholder}
          invalid={invalid}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : null}

      {input.type === "select" ? (
        <div>
          <SelectInput
            id={id}
            value={strValue}
            options={input.options ?? []}
            invalid={invalid}
            disabled={disabled}
            describedBy={describedBy}
            onChange={(v) => onChange(v)}
          />
          <SelectedOptionHint options={input.options ?? []} value={strValue} />
          {input.autodetect ? (
            <DetectRow
              awaiting={isAwaitingDetection}
              detecting={detecting}
              detectedValue={detectedValue}
              options={input.options ?? []}
              canDetect={canDetect}
              disabled={disabled}
              onDetect={onDetect}
            />
          ) : null}
        </div>
      ) : null}

      {input.type === "radio" ? (
        <RadioGroup
          name={input.name}
          options={input.options ?? []}
          value={strValue}
          invalid={invalid}
          labelledBy={labelId}
          describedBy={describedBy}
          onChange={(v) => {
            onChange(v);
            onBlur();
          }}
        />
      ) : null}

      {input.type === "multiselect" ? (
        <CheckboxGroup
          name={input.name}
          options={input.options ?? []}
          value={Array.isArray(value) ? value : []}
          invalid={invalid}
          labelledBy={labelId}
          describedBy={describedBy}
          onChange={(v) => {
            onChange(v);
            onBlur();
          }}
        />
      ) : null}

      {input.type === "toggle" ? (
        <div className="flex items-start justify-between gap-4 rounded-sm border border-outline-variant bg-surface-lowest p-3">
          <div className="min-w-0">
            <p id={labelId} className="md-body-large text-on-surface">
              {input.label}
            </p>
            {input.hint ? (
              <p id={hintId} className="md-body-small mt-0.5 text-on-surface-variant">
                {input.hint}
              </p>
            ) : null}
          </div>
          <Switch
            id={id}
            checked={value === true}
            labelledBy={labelId}
            describedBy={hintId}
            onChange={(v) => onChange(v)}
          />
        </div>
      ) : null}

      {input.type === "file" ? (
        <FileInput
          id={id}
          accept={input.accept}
          fileName={fileName || undefined}
          invalid={invalid}
          describedBy={describedBy}
          onFile={onFile}
        />
      ) : null}

      {/* Counter + error share a row: M3 puts supporting text start, counter end. */}
      {(errId || counterId) && input.type !== "toggle" ? (
        <div className="mt-1.5 flex items-start justify-between gap-3">
          {error ? (
            <p id={errId} role="alert" className="md-body-small flex items-center gap-1 text-error">
              <Icon name="error" size={14} filled />
              {error}
            </p>
          ) : (
            <span />
          )}
          {input.maxLength ? (
            <p
              id={counterId}
              className={`md-body-small shrink-0 tabular-nums ${
                overLimit ? "text-error" : "text-on-surface-variant"
              }`}
            >
              <bdi>
                {len} / {input.maxLength}
              </bdi>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function resolveAutoOption(input: InputDef): string | undefined {
  if (!input.autodetect || !input.options || input.options.length === 0) return undefined;
  const declared = typeof input.default === "string" ? input.default : undefined;
  if (declared && input.options.some((o) => o.value === declared)) return declared;
  return input.options[0].value;
}

/** Pending / detected state for an `autodetect` input. */
function DetectRow({
  awaiting,
  detecting,
  detectedValue,
  options,
  canDetect,
  disabled,
  onDetect,
}: {
  awaiting: boolean;
  detecting: boolean;
  detectedValue?: string;
  options: Option[];
  canDetect: boolean;
  disabled: boolean;
  onDetect: () => void;
}) {
  if (detecting) {
    return (
      <p className="md-body-small mt-2 flex items-center gap-2 text-primary">
        <Spinner size={14} />
        قاعدين نفحص النص…
      </p>
    );
  }

  if (detectedValue) {
    const label = options.find((o) => o.value === detectedValue)?.label ?? detectedValue;
    return (
      <p className="md-body-small mt-2 flex flex-wrap items-center gap-2 text-on-surface-variant">
        <Chip tone="tertiary" icon="auto_awesome">
          اكتشفناها: {label}
        </Chip>
        <span>تكدر تغيّرها إذا مو صحيحة.</span>
      </p>
    );
  }

  if (!awaiting) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Chip tone="neutral" icon="schedule">
        راح نكتشفها وقت التشغيل
      </Chip>
      {canDetect && !disabled ? (
        <button
          type="button"
          onClick={onDetect}
          className="md-state md-label-medium cursor-pointer rounded-full border border-outline px-3 py-1 text-primary"
        >
          اكتشفها هسه
        </button>
      ) : null}
    </div>
  );
}
